import { NavigateFunction } from 'react-router-dom'
import { when } from 'mobx'
import {
  CONFIG_SECOND_DEBTOR,
  CONFIG_SECOND_DEBTOR_SMAVA
} from 'config/utils/config'
import RootStore from 'stores/RootStore'
import { getEntryPointPageName, getSavedPage } from 'utils/entrypoint'
import { isCaptchaOnRender } from 'utils/entrypoint/isCaptchaOnRender'
import { logInfo, logWarning } from 'utils/log'
import { UrlParams } from 'utils/url/url.interface'

import { StartupCommand } from './command.interface'
import { PAGE_WITH_RESOLVED_NAME } from 'constants/components'
import { logAndTrackError } from 'utils/log/logAndTrackError'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'

export enum EntryPoint {
  FIRST = 'first',
  VAS = 'vas',
  VAS_LEAD = 'vasLead',
  BASE_RECEIVED = 'baseReceived',
  DEBTOR_COUNT_AND_RELATIONSHIP_RECEIVED = 'debtorCountAndRelationshipReceived',
  CONTACT_RECEIVED = 'contactReceived',
  FURTHEST = 'furthest',
  FINAL = 'final',
  LEAD_SAVED = 'leadSaved',
  LEAD_ABANDONED = 'leadAbandoned',
  LEAD_ABORTED = 'leadAborted'
}

export class LoadEntryPointCommand implements StartupCommand {
  name = 'LoadEntryPoint'
  dependencies = ['LoadConfiguration', 'InitNavigation']
  timeout = 15000

  private handleFirst = async (
    rootStore: RootStore,
    navigate: NavigateFunction
  ) => {
    const {
      page: { getFirstDisplayedPageName },
      navigation: { setInitiallySeenPage }
    } = rootStore
    const pageName = getFirstDisplayedPageName()

    setInitiallySeenPage(pageName)
    await this.goToPage(getFirstDisplayedPageName(), rootStore, navigate)
  }

  private handleFurthest = async (
    rootStore: RootStore,
    navigate: NavigateFunction
  ) => {
    const {
      page: { getLastDisplayedPageName }
    } = rootStore

    await this.goToPage(getLastDisplayedPageName(), rootStore, navigate)
  }

  private goToPageWithPath =
    (path: string) =>
    async (rootStore: RootStore, navigate: NavigateFunction) => {
      const {
        page: { findFirstPageWithPath },
        navigation: { setInitiallySeenPage }
      } = rootStore
      const pageName = await findFirstPageWithPath(path)

      if (!pageName) {
        await this.handleFurthest(rootStore, navigate)
        return
      }

      setInitiallySeenPage(pageName)
      await this.goToPage(pageName, rootStore, navigate)
    }

  private handleFinal = async (
    rootStore: RootStore,
    navigate: NavigateFunction,
    context?: string,
    resume?: string
  ) => {
    const { finalize } = rootStore.traversal
    const { config } = rootStore.page
    // Do NOT destructure captchaFlowStatus – keeping reference to rootStore.captcha preserves MobX tracking in when()
    const { captcha } = rootStore

    if (isCaptchaOnRender({ context, resume })) {
      try {
        await when(
          () =>
            captcha.captchaFlowStatus === 'success' ||
            captcha.captchaFlowStatus === 'error',
          { timeout: 120000 }
        )
      } catch (err) {
        logAndTrackError(new Error('Captcha on final timeout or error'))
        trackInAmplitude(AmplitudeEvent.CaptchaTimeoutError, {
          final: true,
          error: String(err),
          errObject: err
        })
      }
    }

    try {
      if (
        config.name !== CONFIG_SECOND_DEBTOR &&
        config.name !== CONFIG_SECOND_DEBTOR_SMAVA
      ) {
        await finalize()
        return
      }
      await this.handleFurthest(rootStore, navigate)
    } catch (error) {
      await this.handleFurthest(rootStore, navigate)
    }
  }

  private entryPoints: Record<
    EntryPoint,
    (
      rootStore: RootStore,
      navigate: NavigateFunction,
      context?: string,
      resume?: string
    ) => Promise<void>
  > = {
    [EntryPoint.FIRST]: this.handleFirst,
    [EntryPoint.VAS]: this.handleFirst,
    [EntryPoint.VAS_LEAD]: this.handleFirst,
    [EntryPoint.BASE_RECEIVED]: this.goToPageWithPath('debtors.count'),
    [EntryPoint.DEBTOR_COUNT_AND_RELATIONSHIP_RECEIVED]: this.goToPageWithPath(
      'debtors.primary.contact.email'
    ),
    [EntryPoint.CONTACT_RECEIVED]: this.goToPageWithPath(
      'debtors.primary.personal.birthday'
    ),
    [EntryPoint.FURTHEST]: this.handleFurthest,
    [EntryPoint.LEAD_ABANDONED]: this.handleFurthest,
    [EntryPoint.LEAD_ABORTED]: this.handleFurthest,
    [EntryPoint.LEAD_SAVED]: this.handleFurthest,
    [EntryPoint.FINAL]: this.handleFinal
  }

  // eslint-disable-next-line class-methods-use-this
  private goToPage = async (
    pageName: string,
    rootStore: RootStore,
    navigate: NavigateFunction
  ) => {
    const {
      history,
      navigation: { setCurrentPageInSession }
    } = rootStore

    let resolvedPageName = PAGE_WITH_RESOLVED_NAME.includes(pageName)
      ? pageName
      : await getEntryPointPageName(pageName, rootStore)

    if (pageName !== resolvedPageName) {
      logWarning(`"${pageName}" resolved to "${resolvedPageName}"!`)
    }

    setCurrentPageInSession(resolvedPageName)

    if (resolvedPageName) {
      navigate(resolvedPageName, { replace: true })
    }

    history.replace(resolvedPageName)
  }

  execute = async (
    rootStore: RootStore,
    navigate: NavigateFunction,
    urlParams?: UrlParams
  ) => {
    const savedPage = getSavedPage(rootStore)
    const { initialEntryPoint } = rootStore
    const entryPoint = urlParams?.entryPoint
    const context = urlParams?.context
    const resume = urlParams?.resume

    if (savedPage && initialEntryPoint !== 'final') {
      logInfo(`Reloading current page "${savedPage}"..`)

      if (entryPoint) {
        rootStore.setInitialEntryPoint(entryPoint as EntryPoint)
      }

      await this.goToPage(savedPage, rootStore, navigate)

      return
    }

    if (!savedPage && rootStore.page.config.separateEditPage) {
      await this.goToPage('editPage', rootStore, navigate)

      return
    }

    if (entryPoint) {
      /* handling the entry point from url's handler */
      const handler = this.entryPoints[entryPoint]

      if (handler) {
        logInfo(`Loading entry point from url "${entryPoint}"..`)
        await handler(rootStore, navigate, context, resume)
        return
      }
    }

    logInfo(`Loading entry point "${initialEntryPoint}"..`)

    const handler = this.entryPoints[initialEntryPoint]
    if (handler) {
      await handler(rootStore, navigate, context, resume)
      return
    }

    await this.goToPage(initialEntryPoint, rootStore, navigate)
  }
}

export default LoadEntryPointCommand
